import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

// Adapted from 21st.dev "Aurora voice Hero" (id 8345) — teal/emerald only, no purple palettes.
const RIBBON_COLORS = ['#22d3ee', '#0891b2', '#10b981', '#67e8f9'];

const createSimplex = () => {
  const F2 = 0.5 * (Math.sqrt(3) - 1);
  const G2 = (3 - Math.sqrt(3)) / 6;
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i += 1) p[i] = i;
  for (let i = 255; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = p[i];
    p[i] = p[j];
    p[j] = tmp;
  }
  const perm = new Uint8Array(512);
  const perm12 = new Uint8Array(512);
  const grad3 = [1, 1, -1, 1, 1, -1, -1, -1, 1, 0, -1, 0, 1, 0, -1, 0, 0, 1, 0, -1, 0, 1, 0, -1];
  for (let i = 0; i < 512; i += 1) {
    perm[i] = p[i & 255];
    perm12[i] = perm[i] % 12;
  }
  return (xin, yin) => {
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const t = (i + j) * G2;
    const x0 = xin - (i - t);
    const y0 = yin - (j - t);
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;
    const ii = i & 255;
    const jj = j & 255;
    let n0 = 0;
    let n1 = 0;
    let n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 >= 0) {
      const gi = perm12[ii + perm[jj]] % 8;
      t0 *= t0;
      n0 = t0 * t0 * (grad3[gi * 2] * x0 + grad3[gi * 2 + 1] * y0);
    }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 >= 0) {
      const gi = perm12[ii + i1 + perm[jj + j1]] % 8;
      t1 *= t1;
      n1 = t1 * t1 * (grad3[gi * 2] * x1 + grad3[gi * 2 + 1] * y1);
    }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 >= 0) {
      const gi = perm12[ii + 1 + perm[jj + 1]] % 8;
      t2 *= t2;
      n2 = t2 * t2 * (grad3[gi * 2] * x2 + grad3[gi * 2 + 1] * y2);
    }
    return 70 * (n0 + n1 + n2);
  };
};

const AuroraCanvas = () => {
  const canvasRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const simplex = createSimplex();
    const mouse = { x: 0, y: 0, active: false };
    let frame = 0;
    let time = 0;
    let running = true;
    let visible = true;

    const resize = () => {
      const parent = canvas.parentElement;
      const width = parent ? parent.clientWidth : window.innerWidth;
      const height = parent ? parent.clientHeight : window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
      mouse.active = true;
    };

    const animate = () => {
      if (!running) return;
      if (visible) {
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        ctx.clearRect(0, 0, width, height);
        time += 0.0025;

        RIBBON_COLORS.forEach((color, idx) => {
          ctx.beginPath();
          const grad = ctx.createLinearGradient(0, 0, width, 0);
          grad.addColorStop(0, 'transparent');
          grad.addColorStop(0.5, color);
          grad.addColorStop(1, 'transparent');
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.4;
          ctx.globalAlpha = 0.42;

          for (let x = 0; x <= width; x += 8) {
            const dx = x - mouse.x;
            const dy = height / 2 - mouse.y;
            const dist = Math.hypot(dx, dy);
            const boost = mouse.active ? 1 + (1 - Math.min(1, dist / 520)) * 0.28 : 1;
            const noise = simplex(x * 0.008 * boost, idx * 900 + time);
            const pulse = Math.sin(time * 0.28 + idx) * 0.05 + 0.95;
            const y = height * 0.48 + noise * Math.min(140, height * 0.18) * pulse * boost;
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        });
        ctx.globalAlpha = 1;
      }
      frame = window.requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry && entry.isIntersecting);
      },
      { threshold: 0.05 }
    );
    observer.observe(canvas);

    return () => {
      running = false;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      observer.disconnect();
    };
  }, [reduce]);

  if (reduce) return null;

  return <canvas ref={canvasRef} className="hero-aurora-canvas" aria-hidden="true" />;
};

export default AuroraCanvas;
